import { FC, Suspense, lazy } from "react";
import { useEffect } from "react";
import { Navigate, useNavigate, Route, Routes } from "react-router-dom";
import { getCSSVariableValue } from "../../_metronic/assets/ts/_utils";
import { WithChildren } from "../../_metronic/helpers";
import { MasterLayout } from "../../_metronic/layout/MasterLayout";
import { HomePage } from "../pages/Homepage/HomePage";
import { MenuTestPage } from "../pages/MenuTestPage";
import { DashboardWrapper } from "../pages/dashboard/DashboardWrapper";
import { localStorageItem } from "../../app/modules/utils/LocalStorage";

const PrivateRoutes = () => {
  const navigate = useNavigate();

  // Common Management Modules
  const TrainingPlanManagement = lazy(() => import("../modules/training-plan-management"));
  const SpecialtyList = lazy(() => import("../modules/training-plan-management/components/SpecialtyList"));
  const CourseDirectorList = lazy(() => import("../modules/training-plan-management/components/CourseDirectorList"));
  const DepartmentList = lazy(() => import("../modules/training-plan-management/components/DepartmentList"));

  // Hospital-School Training Modules (Phase 1)
  const StudentManagement = lazy(() => import("../modules/hospital-training/student-management"));
  const ContractManagement = lazy(() => import("../modules/hospital-training/contract-management"));
  const InstructorManagement = lazy(() => import("../modules/hospital-training/instructor-management"));
  const PracticeSessionManagement = lazy(() => import("../modules/hospital-training/practice-session-management"));
  const PracticeClassManagement = lazy(() => import("../modules/hospital-training/practice-class-management"));

  // Technical Training Modules
  const TechnicalTrainingLayout = lazy(() => import("../modules/technical-training/TechnicalTrainingLayout"));
  const ClassManagement = lazy(() => import("../modules/technical-training/class-management/ClassManagement"));
  const TrainingDocumentManagement = lazy(() => import("../modules/technical-training/training-documents/TrainingDocumentManagement"));
  const CertificateTemplateManagement = lazy(() => import("../modules/technical-training/certificate-templates/CertificateTemplateManagement"));
  const IssuedCertificateManagement = lazy(() => import("../modules/technical-training/issued-certificates/IssuedCertificateManagement"));

  // Continuous Training Modules
  const ContinuousTrainingLayout = lazy(() => import("../modules/continuous-training/ContinuousTrainingLayout"));
  const ContinuousClassManagement = lazy(() => import("../modules/continuous-training/class-management/ClassManagement"));
  const ContinuousTrainingDocumentManagement = lazy(() => import("../modules/continuous-training/training-documents/TrainingDocumentManagement"));
  const ContinuousCertificateTemplateManagement = lazy(() => import("../modules/continuous-training/certificate-templates/CertificateTemplateManagement"));
  const ContinuousIssuedCertificateManagement = lazy(() => import("../modules/continuous-training/issued-certificates/IssuedCertificateManagement"));

  // Clinical Practice Training Modules (KB, CB) - Reusing Hospital Training Components
  const ClinicalPracticeClassManagement = lazy(() => import("../modules/clinical-practice-training/practice-class-management"));
  const ClinicalPracticeSessionManagement = lazy(() => import("../modules/clinical-practice-training/practice-session-management"));
  const ClinicalPracticeContractManagement = lazy(() => import("../modules/clinical-practice-training/contract-management"));

  const listMenu = localStorageItem.get("id-token-decode")?.menus?.["oceantech-training-management-server"] || [];
  useEffect(() => {
    // Temporarily disabled menu check for development
    // if (!listMenu?.[0]?.subs?.length) {
    //   navigate("/error/403");
    // } else {
    //   // navigate(`${listMenu?.[0]?.subs?.[0]?.uri}`);
    // }
  }, [listMenu])

  return (
    <Routes>
      <Route index element={<Navigate to="/training-plan-management" replace />} />
      <Route path="/*" element={<Navigate to="/training-plan-management" replace />} />
      <Route path="home" element={<HomePage />} />
      <Route element={<MasterLayout />}>
        <Route path="auth/*" element={<Navigate to="/home" />} />
        <Route path="dashboard" element={<DashboardWrapper />} />
        <Route path="menu-test" element={<MenuTestPage />} />

        {/* Training Plan Management */}
        <Route
          path="training-plan-management"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <TrainingPlanManagement />
            </Suspense>
          }
        />

        {/* Specialty Management */}
        <Route
          path="specialties"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <SpecialtyList />
            </Suspense>
          }
        />

        {/* Course Director Management */}
        <Route
          path="course-directors"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <CourseDirectorList />
            </Suspense>
          }
        />

        {/* Department Management */}
        <Route
          path="departments"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <DepartmentList />
            </Suspense>
          }
        />

        {/* ========== HOSPITAL-SCHOOL TRAINING (Phase 1 - Viện Trường) ========== */}

        {/* Default redirect for Hospital Training */}
        <Route path="hospital-training" element={<Navigate to="/hospital-training/practice-classes" replace />} />

        {/* Student Management - Quản lý Học viên thực hành */}
        <Route
          path="hospital-training/students"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <StudentManagement />
            </Suspense>
          }
        />

        {/* Contract Management - Quản lý Hợp đồng thực hành */}
        <Route
          path="hospital-training/contracts"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <ContractManagement />
            </Suspense>
          }
        />

        {/* Instructor Management - Quản lý Giảng viên hướng dẫn */}
        <Route
          path="hospital-training/instructors"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <InstructorManagement />
            </Suspense>
          }
        />

        {/* Practice Session Management - Quản lý Đợt thực hành */}
        <Route
          path="hospital-training/practice-sessions"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <PracticeSessionManagement />
            </Suspense>
          }
        />

        {/* Practice Class Management - Quản lý Lớp thực hành */}
        <Route
          path="hospital-training/practice-classes"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <PracticeClassManagement />
            </Suspense>
          }
        />

        {/* ========== TECHNICAL TRAINING (Phase 2 - Đào tạo kỹ thuật chuyên môn) ========== */}

        <Route
          path="technical-training"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <TechnicalTrainingLayout />
            </Suspense>
          }
        >
          {/* Class Management - NEW */}
          <Route
            path="classes"
            element={
              <Suspense fallback={<div>Loading...</div>}>
                <ClassManagement />
              </Suspense>
            }
          />

          {/* Training Documents Management - NEW */}
          <Route
            path="documents"
            element={
              <Suspense fallback={<div>Loading...</div>}>
                <TrainingDocumentManagement />
              </Suspense>
            }
          />

          {/* Certificate Templates Management - NEW */}
          <Route
            path="certificate-templates"
            element={
              <Suspense fallback={<div>Loading...</div>}>
                <CertificateTemplateManagement />
              </Suspense>
            }
          />

          {/* Issued Certificates Management - NEW */}
          <Route
            path="certificates"
            element={
              <Suspense fallback={<div>Loading...</div>}>
                <IssuedCertificateManagement />
              </Suspense>
            }
          />

          {/* Default redirect to classes */}
          <Route index element={<Navigate to="classes" />} />
        </Route>

        {/* ========== CONTINUOUS TRAINING (Phase 3 - Đào tạo liên tục) ========== */}

        <Route
          path="continuous-training"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <ContinuousTrainingLayout />
            </Suspense>
          }
        >
          {/* Class Management */}
          <Route
            path="classes"
            element={
              <Suspense fallback={<div>Loading...</div>}>
                <ContinuousClassManagement />
              </Suspense>
            }
          />

          {/* Training Documents Management */}
          <Route
            path="documents"
            element={
              <Suspense fallback={<div>Loading...</div>}>
                <ContinuousTrainingDocumentManagement />
              </Suspense>
            }
          />

          {/* Certificate Templates Management */}
          <Route
            path="certificate-templates"
            element={
              <Suspense fallback={<div>Loading...</div>}>
                <ContinuousCertificateTemplateManagement />
              </Suspense>
            }
          />

          {/* Issued Certificates Management */}
          <Route
            path="certificates"
            element={
              <Suspense fallback={<div>Loading...</div>}>
                <ContinuousIssuedCertificateManagement />
              </Suspense>
            }
          />

          {/* Default redirect to classes */}
          <Route index element={<Navigate to="classes" />} />
        </Route>

        {/* ========== CLINICAL PRACTICE TRAINING (Phase 4 - Hướng dẫn thực hành KB/CB) ========== */}

        {/* Practice Class Management - Quản lý Lớp thực hành KB, CB */}
        <Route
          path="clinical-practice/practice-classes"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <ClinicalPracticeClassManagement />
            </Suspense>
          }
        />

        {/* Practice Session Management - Quản lý Đợt thực hành KB, CB */}
        <Route
          path="clinical-practice/practice-sessions"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <ClinicalPracticeSessionManagement />
            </Suspense>
          }
        />

        {/* Contract Management - Quản lý Hợp đồng KB, CB */}
        <Route
          path="clinical-practice/contracts"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <ClinicalPracticeContractManagement />
            </Suspense>
          }
        />

        {/* Page Not Found */}
        <Route path="*" element={<Navigate to="/error/404" />} />
      </Route>
    </Routes>

  );
};
export { PrivateRoutes };

